import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { Icon } from "@/components/ui/Icon";
import { useMemoOrigin } from "@/hooks/useMemoOrigin";
import type { ViewBoard, ViewRow } from "@/lib/boardView";
import { memoOriginEligible, memoOriginLabel } from "@/lib/memoOrigin";
import { t } from "@/lib/strings";

/** Quiet breadcrumb linking a memo-origin to-do back to the exact bullet in its
 * Protokoll (`Serie · Datum · Abschnitt › Zwischenabschnitt`). Renders nothing
 * unless this is a top-level to-do on an Aufgaben board AND the viewer may open
 * the memo (the backend omits the origin otherwise, so a non-participant never
 * sees it). Shared by the desktop panel and the mobile sheet so the two can't
 * drift. */
export function MemoOriginBreadcrumb({ board, row }: { board: ViewBoard; row: ViewRow }) {
  const navigate = useNavigate();
  const [hover, setHover] = useState(false);
  const eligible = memoOriginEligible(board, row);
  const origin = useMemoOrigin(eligible ? row.raw?.id : undefined);
  if (!eligible || !origin) return null;

  return (
    <button
      type="button"
      title={t.memoOriginTooltip}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() =>
        void navigate({
          to: "/memo/$memoId",
          params: { memoId: origin.memo_id },
          search: { entry: origin.memo_entry_id } as Record<string, string>,
        })
      }
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        maxWidth: "100%",
        marginBottom: 18,
        padding: 0,
        background: "none",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        fontSize: 12,
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        color: hover ? "var(--accent)" : "var(--ink-3)",
      }}
    >
      <Icon name="notebook-text" size={14} color={hover ? "var(--accent)" : "var(--ink-3)"} />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textDecoration: hover ? "underline" : "none",
        }}
      >
        {memoOriginLabel(origin)}
      </span>
    </button>
  );
}
